import React, { useState, useEffect } from "react"
import { useAuth } from "../../AuthContext"
import { updateProfile, changePassword } from "../../api/apiClient"

// Helper component for displaying success/error messages
const StatusMessage = ({ message, type }) => {
  if (!message) return null
  const baseClasses = "p-3 mb-4 rounded-lg font-medium text-sm"
  const classes =
    type === "success"
      ? `${baseClasses} bg-green-100 text-green-700`
      : `${baseClasses} bg-red-100 text-red-700`
  return <div className={classes}>{message}</div>
}

const UserProfile = () => {
  const { currentUser, updateProfileData, logout } = useAuth()

  // --- Profile Form State ---
  const [profileForm, setProfileForm] = useState({
    name: "",
    phone: "",
    company: "",
  })
  const [isProfileSubmitting, setIsProfileSubmitting] = useState(false)
  const [profileStatus, setProfileStatus] = useState({ message: "", type: "" })

  // --- Password Form State ---
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  })
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false)
  const [passwordStatus, setPasswordStatus] = useState({
    message: "",
    type: "",
  })

  // --- Effect 1: Initialize form state when currentUser loads or changes ---
  useEffect(() => {
    if (currentUser) {
      // Set the local form state using data from the global context
      setProfileForm({
        name: currentUser.name || "",
        phone: currentUser.phone || "",
        company: currentUser.company || "",
      })
    }
  }, [currentUser])

  // --- Handlers ---

  const handleProfileChange = e => {
    const { name, value } = e.target
    setProfileForm(prev => ({ ...prev, [name]: value }))
  }

  const handlePasswordChange = e => {
    const { name, value } = e.target
    setPasswordForm(prev => ({ ...prev, [name]: value }))
  }

  // --- Submission Logic ---

  const handleProfileSave = async e => {
    e.preventDefault()
    setProfileStatus({ message: "", type: "" })
    setIsProfileSubmitting(true)

    try {
      // API call to update profile
      await updateProfile(profileForm)

      // CRITICAL: Refresh the global user context after a successful save
      await updateProfileData()

      setProfileStatus({
        message: "Profile updated successfully!",
        type: "success",
      })
    } catch (error) {
      const errorMessage = error.message.includes("API Error")
        ? "Failed to save profile. Please check input."
        : error.message
      setProfileStatus({ message: errorMessage, type: "error" })
    } finally {
      setIsProfileSubmitting(false)
    }
  }

  const handlePasswordSave = async e => {
    e.preventDefault()
    setPasswordStatus({ message: "", type: "" })
    setIsPasswordSubmitting(true)

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus({
        message: "New passwords do not match.",
        type: "error",
      })
      setIsPasswordSubmitting(false)
      return
    }

    try {
      await changePassword(passwordForm)
      setPasswordStatus({
        message: "Password changed successfully! Please log in again.",
        type: "success",
      })

      // Clear password fields immediately
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      })

      // Force user to re-login after password change for security
      setTimeout(() => logout(), 1500)
    } catch (error) {
      const errorMessage = error.message.includes("400")
        ? "Invalid current password or password complexity requirements not met."
        : error.message
      setPasswordStatus({ message: errorMessage, type: "error" })
    } finally {
      setIsPasswordSubmitting(false)
    }
  }

  // Safely access email for the disabled field
  const email = currentUser?.email || ""

  return (
    <div className="space-y-6">
      {/* 1. BASIC INFORMATION FORM */}
      <form
        onSubmit={handleProfileSave}
        className="bg-white p-6 rounded-xl shadow-lg"
      >
        <h3 className="text-xl font-semibold mb-4 text-gray-700">
          Basic Information
        </h3>
        <StatusMessage {...profileStatus} />

        <div className="space-y-3">
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="name"
          >
            Full Name
          </label>
          <input
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            name="name"
            id="name"
            value={profileForm.name}
            onChange={handleProfileChange}
            placeholder="Full Name"
            required
            disabled={isProfileSubmitting}
          />

          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="email"
          >
            Email (Cannot be changed)
          </label>
          <input
            className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
            value={email}
            placeholder="Email"
            disabled
          />

          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="phone"
          >
            Phone
          </label>
          <input
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            name="phone"
            id="phone"
            value={profileForm.phone}
            onChange={handleProfileChange}
            placeholder="Phone"
            disabled={isProfileSubmitting}
          />

          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="company"
          >
            Company
          </label>
          <input
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            name="company"
            id="company"
            value={profileForm.company}
            onChange={handleProfileChange}
            placeholder="Company"
            disabled={isProfileSubmitting}
          />
        </div>
        <button
          type="submit"
          className="mt-5 px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition duration-150 shadow-md disabled:bg-indigo-400"
          disabled={isProfileSubmitting}
        >
          {isProfileSubmitting ? "Saving..." : "Save Profile"}
        </button>
      </form>

      {/* 2. CHANGE PASSWORD FORM */}
      <form
        onSubmit={handlePasswordSave}
        className="bg-white p-6 rounded-xl shadow-lg"
      >
        <h3 className="text-xl font-semibold mb-4 text-gray-700">
          Change Password
        </h3>
        <StatusMessage {...passwordStatus} />

        <div className="space-y-3">
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="currentPassword"
          >
            Current Password
          </label>
          <input
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-gray-500 focus:border-gray-500"
            name="currentPassword"
            id="currentPassword"
            value={passwordForm.currentPassword}
            onChange={handlePasswordChange}
            type="password"
            placeholder="Current Password"
            required
            disabled={isPasswordSubmitting}
          />

          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="newPassword"
          >
            New Password
          </label>
          <input
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-gray-500 focus:border-gray-500"
            name="newPassword"
            id="newPassword"
            value={passwordForm.newPassword}
            onChange={handlePasswordChange}
            type="password"
            placeholder="New Password"
            required
            disabled={isPasswordSubmitting}
          />

          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="confirmPassword"
          >
            Confirm New Password
          </label>
          <input
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-gray-500 focus:border-gray-500"
            name="confirmPassword"
            id="confirmPassword"
            value={passwordForm.confirmPassword}
            onChange={handlePasswordChange}
            type="password"
            placeholder="Confirm New Password"
            required
            disabled={isPasswordSubmitting}
          />
        </div>
        <button
          type="submit"
          className="mt-5 px-6 py-2 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-700 transition duration-150 shadow-md disabled:bg-gray-400"
          disabled={isPasswordSubmitting}
        >
          {isPasswordSubmitting ? "Changing..." : "Change Password"}
        </button>
      </form>
    </div>
  )
}

export default UserProfile
